import { metricShares, WORD_LENGTH_COLORS, WORD_LENGTHS, wordLengthLabel } from '@/components/nWords/nWordShare';
import type { NWordSummary } from '@/types/nWords';

const GROUPS = [
  { id: 'sales', label: 'Sales' },
  { id: 'spend', label: 'Spend' },
  { id: 'clicks', label: 'Clicks' },
] as const;

const TICKS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

export function NWordDistributionChart({ summary }: { summary: NWordSummary[] }) {
  const shares = metricShares(summary);

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-x-4 gap-y-2">
        {WORD_LENGTHS.map((wordCount, index) => (
          <span key={wordCount} className="inline-flex items-center gap-1.5 text-xs text-foreground-muted">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: WORD_LENGTH_COLORS[index] }}
              aria-hidden
            />
            {wordLengthLabel(wordCount)}
          </span>
        ))}
      </div>
      <div className="relative mt-2 h-64">
        <div className="absolute inset-x-0 bottom-0 top-3">
        {TICKS.map((tick) => (
          <div
            key={tick}
            className="pointer-events-none absolute left-10 right-0 border-t border-border/70"
            style={{ bottom: `${tick}%` }}
          >
            <span className="absolute right-full mr-2 -translate-y-1/2 text-[10px] text-foreground-subtle">
              {tick}%
            </span>
          </div>
        ))}
        <div className="absolute inset-y-0 left-10 right-0 flex items-end">
          {GROUPS.map((group) => (
            <div key={group.id} className="flex h-full flex-1 items-end justify-center gap-1 px-2">
              {shares[group.id].map((share, index) => (
                <div key={WORD_LENGTHS[index]} className="relative h-full w-3 max-w-[14%]">
                  <div
                    className="absolute bottom-0 left-0 right-0 rounded-t-sm"
                    style={{
                      height: `${Math.max(0, Math.min(100, share))}%`,
                      backgroundColor: WORD_LENGTH_COLORS[index],
                    }}
                    title={`${wordLengthLabel(WORD_LENGTHS[index])} ${group.label} ${share.toFixed(1)}%`}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-3 pl-10 text-center text-xs text-foreground-muted">
        {GROUPS.map((group) => (
          <span key={group.id}>{group.label}</span>
        ))}
      </div>
      <ul className="sr-only">
        {GROUPS.map((group) =>
          shares[group.id].map((share, index) => (
            <li key={`${group.id}-${WORD_LENGTHS[index]}`}>
              {wordLengthLabel(WORD_LENGTHS[index])} {group.label} {share.toFixed(1)} percent
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
